import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { ProcessItemViewModel } from "../../../../models/Process";
import { Dispatch, SetStateAction } from "react";
import NveModal from "./NveModal";
import CatalogAttributesModal from "./CatalogAttributesModal";
import TaxTreatmentModal from "./TaxTreatmentModal";
import { currencyString } from "../../../../services/utils";

const ProcessItem = ({
  i,
  error,
  setError,
  index,
  items,
  setItems,
  clientCnpj,
  getExporter,
  country,
}: {
  i: ProcessItemViewModel;
  error: string | undefined;
  setError: Dispatch<SetStateAction<string | undefined>>;
  index: number;
  items: ProcessItemViewModel[];
  setItems: Dispatch<SetStateAction<ProcessItemViewModel[]>>;
  clientCnpj?: string;
  getExporter?: () => string;
  country?: string;
}) => {
  const nve = useDisclosure();
  const attrs = useDisclosure();
  const tax = useDisclosure();
  const nveReady =
    (items[index].ncm ?? "").replace(/\D/g, "").length === 8 && (items[index].product ?? "").trim().length > 0;

  return (
    <Flex
      gap="16px"
      alignItems="flex-end"
      flexDir={{ base: "column", md: "row" }}
    >
      <FormControl position="relative">
        <FormLabel>NCM: </FormLabel>
        <Input
          value={items[index].ncm}
          onChange={(e) =>
            setItems((items) => {
              const newItems = [...items];
              newItems[index].ncm = e.target.value;
              return newItems;
            })
          }
          name="ncm"
          placeholder="NCM"
        />
        <Text color="red.300" h="24px"></Text>
      </FormControl>
      <FormControl position="relative">
        <FormLabel>Quantidade: </FormLabel>
        <Input
          value={items[index].quantity?.replace("e", "")}
          onChange={(e) => {
            setError(undefined);
            setItems((items) => {
              const newItems = [...items];
                newItems[index].quantity = e.target.value.replace(/[^0-9,]+/g, "");
              return newItems;
            });
          }}
          borderColor={
            error && (items[index].quantity?.length ?? 0) === 0
              ? "red"
              : undefined
          }
          name="quantity"
          placeholder="Quantidade"
        />
        <Text color="red.300" h="24px">
          {error &&
            (items[index].quantity?.length ?? 0) === 0 &&
            "Campo obrigatório"}
        </Text>
      </FormControl>
      <FormControl position="relative">
        <FormLabel>Produto: </FormLabel>
        <Input
          value={items[index].product}
          onChange={(e) => {
            setError(undefined);
            setItems((items) => {
              const newItems = [...items];
              newItems[index].product = e.target.value;
              return newItems;
            });
          }}
          borderColor={
            error && (items[index].product?.length ?? 0) === 0
              ? "red"
              : undefined
          }
          name="product"
          placeholder="Produto"
        />
        <Text color="red.300" h="24px">
          {error &&
            (items[index].product?.length ?? 0) === 0 &&
            "Campo obrigatório"}
        </Text>
      </FormControl>
      <FormControl position="relative">
        <FormLabel>Valor unitário: </FormLabel>
        <Input
          value={items[index].unitValue}
          onChange={(e) => {
            setError(undefined);
            setItems((items) => {
              const newItems = [...items];
              newItems[index].unitValue = currencyString(e.target.value);
              return newItems;
            });
          }}
          borderColor={
            error && (items[index].unitValue?.length ?? 0) === 0
              ? "red"
              : undefined
          }
          name="unitValue"
          placeholder="Valor unitário"
        />

        <Text color="red.300" h="24px">
          {error &&
            (items[index].unitValue?.length ?? 0) === 0 &&
            "Campo obrigatório"}
        </Text>
      </FormControl>
      <FormControl position="relative">
        <FormLabel>Peso líquido (kg): </FormLabel>
        <Input
          value={items[index].netWeight ?? ""}
          onChange={(e) => {
            setError(undefined);
            const v = e.target.value.replace(/[^0-9,.]+/g, "");
            setItems((items) => {
              const newItems = [...items];
              newItems[index] = { ...newItems[index], netWeight: v };
              return newItems;
            });
          }}
          name="netWeight"
          placeholder="Peso líquido (DUIMP)"
        />
        <Text color="red.300" h="24px"></Text>
      </FormControl>
      <Tooltip label={nveReady ? "NVE do produto" : "Informe NCM (8 dígitos) e produto para escolher a NVE"}>
        <Button
          onClick={nve.onOpen}
          isDisabled={!nveReady}
          variant="outline"
          size="sm"
          h="40px"
          position="relative"
          bottom="24px"
          color="var(--icon-color)"
          borderColor="var(--icon-color)"
        >
          NVE
        </Button>
      </Tooltip>
      <Tooltip label={nveReady ? "Atributos do Catálogo de Produtos (Portal Único)" : "Informe NCM (8 dígitos) e produto para preencher os atributos"}>
        <Button
          onClick={attrs.onOpen}
          isDisabled={!nveReady}
          variant="outline"
          size="sm"
          h="40px"
          position="relative"
          bottom="24px"
          color="var(--icon-color)"
          borderColor="var(--icon-color)"
        >
          Atributos
        </Button>
      </Tooltip>
      <Tooltip label={nveReady && country ? "Tributos do item (TTCE: antidumping etc.)" : "Informe NCM, produto e país de origem para escolher os tributos"}>
        <Button
          onClick={tax.onOpen}
          isDisabled={!nveReady || !country}
          variant="outline"
          size="sm"
          h="40px"
          position="relative"
          bottom="24px"
          color="var(--icon-color)"
          borderColor="var(--icon-color)"
        >
          Tributos
        </Button>
      </Tooltip>
      {tax.isOpen && (
        <TaxTreatmentModal
          isOpen={tax.isOpen}
          onClose={tax.onClose}
          ncm={items[index].ncm ?? ""}
          product={items[index].product ?? ""}
          exporter={getExporter?.() ?? ""}
          country={country ?? ""}
        />
      )}
      {attrs.isOpen && (
        <CatalogAttributesModal
          isOpen={attrs.isOpen}
          onClose={attrs.onClose}
          ncm={items[index].ncm ?? ""}
          product={items[index].product ?? ""}
          cnpj={clientCnpj}
        />
      )}
      {nve.isOpen && (
        <NveModal
          isOpen={nve.isOpen}
          onClose={nve.onClose}
          ncm={items[index].ncm ?? ""}
          product={items[index].product ?? ""}
        />
      )}
      <IconButton
        onClick={() =>
          setItems((items) => {
            const newItems = [...items];
            newItems.splice(index, 1);
            return newItems;
          })
        }
        backgroundColor="var(--icon-color)"
        color="white"
        aria-label="excluir"
        position="relative"
        bottom="24px"
        icon={<FaTrash />}
      />
    </Flex>
  );
};

export default ProcessItem;
